import * as Yup from 'yup';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { formatUnitCost, Input } from '@api/Inputs/Input';
import { buildInputsById, getRecipeCost, Recipe } from '@api/Recipes/Recipe';
import { buildRecipesById, ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { CostSummaryBar } from '@screens/Catalog/components/CostSummaryBar';
import { EmptyItemsBox } from '@screens/Catalog/components/EmptyItemsBox';

interface ModalSaveModifierGroupProps {
    modalSaveModifierGroup: OpenModalHookType;
    modifierGroup?: ModifierGroup;
    recipesList: Recipe[];
    inputsList: Input[];
    onSaveModifierGroup: (modifierGroup: ModifierGroup) => void;
}

const defaultModifierGroupValues: any = {
    id: undefined,
    name: '',
    options: []
};

const saveModifierGroupSchema = Yup.object({
    id: Yup.string().nullable(),
    name: Yup.string().required('Nombre requerido'),
    options: Yup.array()
        .of(
            Yup.object().shape({
                id: Yup.string().nullable(),
                name: Yup.string().required('Nombre requerido'),
                recipeId: Yup.string().required('Receta requerida'),
                quantityPerSelection: Yup.number()
                    .typeError('Cantidad requerida')
                    .required('Cantidad requerida')
                    .moreThan(0, 'Debe ser mayor a 0'),
                extraCost: Yup.number().typeError('Costo requerido').required('Costo requerido').min(0, 'Mínimo $0'),
                status: Yup.boolean()
            })
        )
        .min(1, 'Agrega al menos una opción al grupo')
});

export const ModalSaveModifierGroup: React.FunctionComponent<ModalSaveModifierGroupProps> = (props) => {
    const { modalSaveModifierGroup, modifierGroup, recipesList, inputsList, onSaveModifierGroup } = props;

    const inputsById = useMemo(() => buildInputsById(inputsList), [inputsList]);
    const recipesById = useMemo(() => buildRecipesById(recipesList), [recipesList]);
    // Solo recetas activas marcadas como disponibles para modificadores
    const selectableRecipes = useMemo(
        () =>
            recipesList
                .filter((recipe) => recipe.availableForModifiers && recipe.status)
                .sort((a, b) => a.name.localeCompare(b.name, 'es')),
        [recipesList]
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = useForm<ModifierGroup>({
        resolver: yupResolver(saveModifierGroupSchema),
        defaultValues: defaultModifierGroupValues
    });

    const {
        fields: optionFields,
        append: appendOption,
        remove: removeOption
    } = useFieldArray({
        control,
        name: 'options'
    });

    const watchedOptions = watch('options') || [];
    const extrasCount = watchedOptions.filter((option) => Number(option?.extraCost) > 0).length;

    const addOption = () =>
        appendOption({
            // El id definitivo lo asigna ModifierGroupServ.saveModifierGroup
            id: '',
            name: '',
            recipeId: '',
            quantityPerSelection: 1,
            extraCost: 0,
            status: true
        });

    const handleClose = () => {
        reset(defaultModifierGroupValues);
        modalSaveModifierGroup.toggle();
    };

    const onSaveModifierGroupForm: SubmitHandler<ModifierGroup> = (values) => {
        onSaveModifierGroup(values);
    };

    useEffect(() => {
        if (modalSaveModifierGroup.modalOpen) {
            if (modifierGroup && modifierGroup.id) {
                reset({
                    id: modifierGroup.id,
                    name: modifierGroup.name || '',
                    options: modifierGroup.options
                        ? modifierGroup.options.map((option) => ({
                              id: option.id,
                              name: option.name || '',
                              recipeId: option.recipeId,
                              quantityPerSelection: option.quantityPerSelection || 0,
                              extraCost: option.extraCost || 0,
                              status: option.status ?? true
                          }))
                        : []
                });
            } else {
                reset(defaultModifierGroupValues);
            }
        }
    }, [modalSaveModifierGroup.modalOpen, modifierGroup, reset]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveModifierGroup.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-modifier-group-title"
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle id="modal-save-modifier-group-title">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {watch('id') ? 'Editar modificador' : 'Nuevo modificador'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Cada opción consume una receta · costo extra $0 = incluida en el precio, mayor a $0 =
                            extra cobrado
                        </Typography>
                    </Box>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="save-modifier-group-form" onSubmit={handleSubmit(onSaveModifierGroupForm)}>
                    <Grid container spacing={2} pt={1}>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        required
                                        label="Nombre del grupo"
                                        variant="outlined"
                                        placeholder='Ej. "Sabor de salsas", "Selección de piezas"'
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography fontSize={17} fontWeight={600}>
                                    Opciones del grupo
                                </Typography>
                                {optionFields.length > 0 && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addOption}
                                    >
                                        Agregar opción
                                    </Button>
                                )}
                            </Stack>
                            {errors.options && !Array.isArray(errors.options) && (
                                <FormHelperText error>{(errors.options as any)?.message}</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {optionFields.length === 0 && (
                                <EmptyItemsBox
                                    text="Este grupo aún no tiene opciones"
                                    buttonLabel="Agregar opción"
                                    onAdd={addOption}
                                />
                            )}
                            {optionFields.map((field, index) => {
                                const currentRecipeId = watch(`options.${index}.recipeId`);
                                const currentRecipe = currentRecipeId ? recipesById.get(currentRecipeId) : undefined;
                                const missingRecipe = !!currentRecipeId && !currentRecipe;
                                const notSelectable =
                                    !!currentRecipe && !selectableRecipes.some((r) => r.id === currentRecipeId);
                                const quantity = Number(watch(`options.${index}.quantityPerSelection`)) || 0;
                                const consumedCost = currentRecipe
                                    ? quantity * getRecipeCost(currentRecipe.items, inputsById)
                                    : 0;
                                // Opciones sintéticas para referencias rotas o no seleccionables
                                const options: Recipe[] = missingRecipe
                                    ? [
                                          { id: currentRecipeId, name: `Receta eliminada (${currentRecipeId})` } as Recipe,
                                          ...selectableRecipes
                                      ]
                                    : notSelectable && currentRecipe
                                    ? [currentRecipe, ...selectableRecipes]
                                    : selectableRecipes;
                                const valueOption = options.find((option) => option.id === currentRecipeId) || null;

                                return (
                                    <Paper key={field.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={3}>
                                                <Controller
                                                    name={`options.${index}.name`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            label="Nombre visible"
                                                            placeholder='Ej. "BBQ"'
                                                            error={!!errors.options?.[index]?.name}
                                                            helperText={
                                                                errors.options?.[index]?.name?.message ||
                                                                'Sin precio en el nombre'
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Controller
                                                    name={`options.${index}.recipeId`}
                                                    control={control}
                                                    render={({ field: { onChange } }) => (
                                                        <Autocomplete
                                                            options={options}
                                                            value={valueOption}
                                                            onChange={(_, option) => onChange(option?.id || '')}
                                                            getOptionLabel={(option) => option.name}
                                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                                            noOptionsText="Sin recetas disponibles para modificadores"
                                                            renderOption={(props, option) => (
                                                                <li {...props} key={option.id}>
                                                                    <Stack>
                                                                        <Typography variant="body2">
                                                                            {option.name}
                                                                        </Typography>
                                                                        {option.items && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {formatUnitCost(
                                                                                    getRecipeCost(
                                                                                        option.items,
                                                                                        inputsById
                                                                                    )
                                                                                )}{' '}
                                                                                de insumos
                                                                            </Typography>
                                                                        )}
                                                                    </Stack>
                                                                </li>
                                                            )}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Receta que consume"
                                                                    required
                                                                    error={
                                                                        !!errors.options?.[index]?.recipeId ||
                                                                        missingRecipe
                                                                    }
                                                                    helperText={
                                                                        errors.options?.[index]?.recipeId?.message ||
                                                                        (missingRecipe &&
                                                                            'Esta receta ya no existe: elige una vigente') ||
                                                                        (notSelectable &&
                                                                            'Receta inactiva o no marcada para modificadores')
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Controller
                                                    name={`options.${index}.quantityPerSelection`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Recetas por selección"
                                                            inputProps={{ step: 'any', min: 0 }}
                                                            error={!!errors.options?.[index]?.quantityPerSelection}
                                                            helperText={
                                                                errors.options?.[index]?.quantityPerSelection
                                                                    ?.message ||
                                                                (currentRecipe &&
                                                                    `Consume ${formatUnitCost(consumedCost)}`)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Controller
                                                    name={`options.${index}.extraCost`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Costo extra"
                                                            inputProps={{ step: 'any', min: 0 }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">$</InputAdornment>
                                                                )
                                                            }}
                                                            error={!!errors.options?.[index]?.extraCost}
                                                            helperText={
                                                                errors.options?.[index]?.extraCost?.message ||
                                                                '$0 = incluida en el precio'
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent={{ xs: 'space-between', md: 'flex-end' }}
                                                    spacing={0.5}
                                                >
                                                    <Controller
                                                        name={`options.${index}.status`}
                                                        control={control}
                                                        render={({ field: { onChange, value } }) => (
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        size="small"
                                                                        checked={!!value}
                                                                        onChange={onChange}
                                                                    />
                                                                }
                                                                label={<Typography variant="body2">Activa</Typography>}
                                                            />
                                                        )}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeOption(index)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Stack width="100%" spacing={1.5}>
                    <CostSummaryBar
                        items={[
                            { label: 'Opciones', value: `${watchedOptions.length}` },
                            { label: 'Con costo extra', value: `${extrasCount}` }
                        ]}
                    />
                    <Box textAlign="center">
                        <Button
                            sx={{ width: 300 }}
                            color="primary"
                            variant="contained"
                            type="submit"
                            form="save-modifier-group-form"
                        >
                            Guardar
                        </Button>
                    </Box>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
